import React from 'react';
import { ELSCommonConfig } from '@els/els-ui-common-react';
import { History } from 'history';
import {
  orderBy,
  uniq
} from 'lodash';
import { RemediationBaseState } from '../hocs/with-remediation-base/withRemediationBase.hoc';
import {
  ActivityTypeMetricsDto,
  HesiExamRemediationSettingsDto,
  QuestionSubmissionDetailsDto,
  QuizSessionQuestionDto,
  RaasAssessmentMetricsDto,
  RecommendationAttemptDto,
  RecommendationAttemptStatusDto,
  RecommendationContentTypeDto,
  RecommendationDto,
  RecommendationItemDto,
  RemediationActivityTypeDto,
  RemediationActivityTypeSettings,
  StatusWithDueDate,
  RemediationAssignmentWithSourcesDto,
  RemediationAssignmentSourceTypeDto
} from '../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { RecordObject } from '../models/common.models';
import { RoutePath } from '../components/app/app.constants';
import {
  ActivityTypeConfigMap,
  AssessmentPlayerAppLinkOutBody,
  CaseStudyAppLinkOutBody,
  GoalBreadcrumb,
  NavigateToContentProps,
  RemediationContentTypeConfigMap,
  RemediationStatusCountLabel,
  StatusMapConfig
} from '../models/remediation.models';
import {
  appLinkOutAction,
  postAssessmentAction,
  postRecommendationAttemptAction,
  trackNewRelicAction
} from '../components/app/app-state-actions.utilities';
import { getProduct } from './product.utilities';
import { Product } from '../constants/product.constants';
import { NewRelicAnalyticsAction } from '../models/new-relic-analytics.models';
import {
  AppAction,
  Application
} from '../apis/eols-app-link/eols-app-link.constants';
import {
  addSearchParams,
  getParentLinkIdForLinkOut
} from './app.utilities';
import { AppContextState } from '../components/app/AppProvider.component';
import {
  AssessmentGoalDto,
  AssessmentSubmissionDto,
  EolsAssessmentDto
} from '../apis/eols-assessment-service/eols-assessment-service.dtos';
import { AppState } from '../components/app/app-state.types';
import { getAllGoalsAreZero } from '../components/due-date-progress/due-date-progress.utilities';

export const getAssessmentGoals = (eolsAssessment: EolsAssessmentDto): AssessmentGoalDto[] => {
  return eolsAssessment ? eolsAssessment.assessmentGoals : null;
};

/**
 * Gets the HESI exam edition ID from a remediation assignment if it's a HESI source
 * @param remediationAssignment The remediation assignment with sources
 * @returns The edition ID as a number, or null if not a HESI source or no sources exist
 */
export const getHesiEditionId = (remediationAssignment: RemediationAssignmentWithSourcesDto): number => {
  if (!remediationAssignment?.sourceList?.length) {
    return null;
  }

  const hesiSource = remediationAssignment.sourceList.find(source => source.sourceType === RemediationAssignmentSourceTypeDto.HESI_EXAM_EDITION__EDITION_ID);

  return hesiSource ? Number(hesiSource.sourceId) : null;
};

export const getAssessmentSubmissionsFromAttempt = (eolsAssessmentId: number, submissions: AssessmentSubmissionDto[]): AssessmentSubmissionDto[] => {
  if (!submissions) {
    return null;
  }

  return submissions.filter((item) => {
    return item.assessmentId === eolsAssessmentId;
  });
};

export const isRecommendationPartOfGoal = (recommendation: RecommendationDto, assessmentGoal: AssessmentGoalDto): boolean => {
  if (!recommendation || !assessmentGoal) {
    return false;
  }
  return recommendation.assessmentGoalVtwId === assessmentGoal.vtwId && recommendation.assessmentGoalType === assessmentGoal.type;
};

export const getRecommendationsFromGoal = (recommendations: RecommendationDto[], assessmentGoal: AssessmentGoalDto): RecommendationDto[] => {
  return recommendations.filter((recommendation) => {
    return isRecommendationPartOfGoal(recommendation, assessmentGoal);
  });
};

export const getAttemptsFromRecommendation = (recommendationId: number, attempts: RecommendationAttemptDto[]): RecommendationAttemptDto[] => {
  if (!attempts) {
    return null;
  }
  return attempts.filter((item) => {
    return item.recommendationId === recommendationId;
  });
};

export const getTotalCompletedPassed = (recommendation: RecommendationDto, config: StatusMapConfig): number => {
  const attempts = getAttemptsFromRecommendation(recommendation.id, config.recommendationAttempts);

  return attempts.reduce((acc, attempt) => {
    if (
      attempt
      && attempt.status === RecommendationAttemptStatusDto.COMPLETED_PASSED
    ) {
      return acc + 1;
    }
    return acc;
  }, 0);
};

export const getTotalAttempted = (recommendation: RecommendationDto, config: StatusMapConfig): number => {
  const attempts = getAttemptsFromRecommendation(recommendation.id, config.recommendationAttempts);
  return attempts.reduce((acc, attempt) => {
    if (
      attempt
      && [
        RecommendationAttemptStatusDto.COMPLETED_PASSED,
        RecommendationAttemptStatusDto.COMPLETED_FAILED
      ].includes(attempt.status)
    ) {
      return acc + 1;
    }
    return acc;
  }, 0);
};

export const getAssessmentGoalTotals = (props: {
  assessmentGoal: AssessmentGoalDto;
  baseState: RemediationBaseState;
}): ActivityTypeMetricsDto => {

  const {
    assessmentGoal,
    baseState,
  } = props;

  if (!baseState?.assessmentMetrics?.assessmentGoalMetrics) {
    return null;
  }

  const assessmentGoalMetrics = baseState.assessmentMetrics.assessmentGoalMetrics.find((item) => {
    return item.assessmentGoal.vtwId === assessmentGoal.vtwId;
  });

  if (!assessmentGoalMetrics) {
    return null;
  }

  return assessmentGoalMetrics.metrics
};

export const getCategoryGroupNameForGoal = (
  assessmentGoal: AssessmentGoalDto,
  baseState: RemediationBaseState
): string | null => {
  if (!assessmentGoal || !baseState?.assessmentMetrics?.assessmentGoalMetrics) {
    return null;
  }

  const assessmentGoalMetrics = baseState.assessmentMetrics.assessmentGoalMetrics.find((item) => {
    return item.assessmentGoal.vtwId === assessmentGoal.vtwId;
  });

  return assessmentGoalMetrics?.categoryGroupName ?? null;
};

export const getIncompleteAttempts = (
  recommendationAttempts: RecommendationAttemptDto[],
  recommendation: RecommendationDto
): RecommendationAttemptDto[] => {
  if (!recommendationAttempts || !recommendation) {
    return null;
  }
  return recommendationAttempts.filter(({
                                          recommendationId,
                                          status
                                        }: RecommendationAttemptDto) => {
    return recommendationId === recommendation.id
      && status !== RecommendationAttemptStatusDto.COMPLETED_PASSED
      && status !== RecommendationAttemptStatusDto.COMPLETED_FAILED;
  });
};

const getResultsFromRecommendationAttempt = (recommendationAttemptId: number, config: StatusMapConfig): QuestionSubmissionDetailsDto[] => {
  if (!config.quizAttemptDetails) {
    return null;
  }
  return config.quizAttemptDetails.questionSubmissionDetails.filter((item) => {
    return item.recommendationAttemptId === recommendationAttemptId;
  });
};

export const getTotalItemCorrect = (item: RecommendationItemDto, config: StatusMapConfig): number => {
  const attempts = getAttemptsFromRecommendation(item.recommendationId, config.recommendationAttempts);

  if (!attempts || !config.quizAttemptDetails) {
    return 0;
  }
  return attempts.reduce((acc, attempt) => {
    return getResultsFromRecommendationAttempt(attempt.id, config)
      .reduce((_acc, result) => {
        return result.questions.reduce((__acc, question) => {
          if (question.questionVtwId === item.itemId && question.correct) {
            return __acc + 1;
          }
          return __acc;
        }, _acc);
      }, acc);
  }, 0);
};

export const getTotalItemAttempted = (item: RecommendationItemDto, config: StatusMapConfig): number => {
  const attempts = getAttemptsFromRecommendation(item.recommendationId, config.recommendationAttempts);

  if (!attempts || !config.quizAttemptDetails) {
    return 0;
  }
  return attempts.reduce((acc, attempt) => {
    return getResultsFromRecommendationAttempt(attempt.id, config)
      .reduce((_acc, result) => {
        return result.questions.reduce((__acc, question: QuizSessionQuestionDto) => {
          if (question.questionVtwId === item.itemId) {
            return __acc + 1;
          }
          return __acc;
        }, _acc);
      }, acc);
  }, 0);
};

export const getContentTypesFromActivityType = (activityType: RemediationActivityTypeDto, baseState: RemediationBaseState): RecommendationContentTypeDto[] => {
  const activeContentTypes = getActiveContentTypes(baseState);
  return Object.keys(RecommendationContentTypeDto)
    .filter((contentType: RecommendationContentTypeDto) => {
      return RemediationContentTypeConfigMap[contentType]
        && RemediationContentTypeConfigMap[contentType].activityType === activityType
        && activeContentTypes.includes(contentType);
    }) as RecommendationContentTypeDto[];
};

export const getContentTypeMainDisplay = (contentTypes: RecommendationContentTypeDto[], count: number): string => {
  const useSingular = count === 1;
  return orderBy(contentTypes, (contentType) => RemediationContentTypeConfigMap[contentType].sortOrder)
    .map((contentType: RecommendationContentTypeDto) => {
      return useSingular
        ? RemediationContentTypeConfigMap[contentType].titleSingular
        : RemediationContentTypeConfigMap[contentType].titlePlural;
    })
    .join(' & ');
};

export const getContentTypeSubtitleDisplay = (contentTypes: RecommendationContentTypeDto[]): string => {
  return orderBy(contentTypes, (contentType) => RemediationContentTypeConfigMap[contentType].sortOrder)
    .map((contentType: RecommendationContentTypeDto) => {
      return RemediationContentTypeConfigMap[contentType].titlePlural;
    })
    .join(' and/or ');
};

export const getPercent = (
  completed: number,
  total: number
): number => {
  if (!total) {
    return 0;
  }

  let topNum = Number(completed)
  const botNum = Number(total)
  if (topNum > botNum) {
    topNum = botNum
  }

  return (topNum / botNum) * 100;
};

export const convertToPercent = (progress: number): number => {
  if (!progress) {
    return 0;
  }
  if (progress > 1) {
    return 100;
  }
  return Math.floor(progress * 100);
};

export const getQuizAttempt = (recommendations: RecommendationDto[], attempts: RecommendationAttemptDto[]): RecommendationAttemptDto => {

  if (!recommendations || !recommendations.length || !attempts || !attempts.length) {
    return null;
  }

  const quizRecs = recommendations.filter((recommendation) => {
    return recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ;
  });

  if (quizRecs.length > 1) {
    throw new Error('More than one quiz recommendation found');
  }

  if (quizRecs.length === 0) {
    return null;
  }

  const recommendationId = quizRecs[0].id;

  const quizAttempts = attempts.filter((attempt) => {
    return attempt.recommendationId === recommendationId;
  });

  if (quizAttempts.length > 1) {
    throw new Error('More than one quiz attempt found');
  }

  if (quizAttempts.length === 0) {
    return null;
  }

  return quizAttempts[0];

};

export const getQuizAttemptFromBaseState = (baseState: RemediationBaseState): RecommendationAttemptDto => {
  if (!baseState || !baseState.recommendationAttempts) {
    return null;
  }
  return getQuizAttempt(baseState.recommendations, baseState.recommendationAttempts);
};

export const getQuizTotals = (baseState: RemediationBaseState): ActivityTypeMetricsDto => {
  return getMetricCounts(baseState, RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE)
};

export const getMetricCounts = (
  baseState: RemediationBaseState,
  activityType: RemediationActivityTypeDto
): ActivityTypeMetricsDto => {

  if (!baseState || !baseState.assessmentMetrics || !activityType) {
    return null;
  }

  const returnMap: Record<RemediationActivityTypeDto, ActivityTypeMetricsDto> = {
    [RemediationActivityTypeDto.REVIEW_BY_TAXON]: baseState.assessmentMetrics.reviewContent,
    [RemediationActivityTypeDto.APPLY_BY_CONTENT_TYPE]: baseState.assessmentMetrics.applyContent,
    [RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE]: baseState.assessmentMetrics.assessContent,
  }
  return returnMap[activityType] || null;
}

export const getAssessmentGoalTotalsCombined = (props: {
  assessmentGoal: AssessmentGoalDto;
  baseState: RemediationBaseState;
}): ActivityTypeMetricsDto => {

  const {
    assessmentGoal,
    baseState,
  } = props;

  return getAssessmentGoalTotals({
    assessmentGoal,
    baseState
  });
};

export const getTotalProgress = (baseState: RemediationBaseState): number => {
  if (!baseState || !baseState.eolsAssessment) {
    return 0;
  }

  const allGoalsAreZero = getAllGoalsAreZero(baseState);

  if (allGoalsAreZero) {
    return 100;
  }

  return convertToPercent(baseState?.assessmentMetrics?.totalProgress)
};

export const isTotalPLPComplete = (baseState: RemediationBaseState): boolean => {
  const totalProgress = getTotalProgress(baseState);
  return totalProgress >= 100;
};

export const getAssessmentSubmissionByAttempt = (attempt: RecommendationAttemptDto, submissions: AssessmentSubmissionDto[]): AssessmentSubmissionDto => {
  if (!submissions) {
    return null;
  }
  return submissions.find((submission) => {
    return submission.assessmentId === attempt.eolsAssessmentId;
  });
};

export const getBestStatusAttempt = (props: {
  recommendationAttempts: RecommendationAttemptDto[];
  attemptAssessmentSubmissions: AssessmentSubmissionDto[];
}): RecommendationAttemptDto => {
  const {
    recommendationAttempts,
    attemptAssessmentSubmissions
  } = props;
  if (!recommendationAttempts || !recommendationAttempts.length) {
    return null;
  }

  const sortOrder: Record<RecommendationAttemptStatusDto, number> = {
    [RecommendationAttemptStatusDto.COMPLETED_PASSED]: 1,
    [RecommendationAttemptStatusDto.COMPLETED_FAILED]: 2,
    [RecommendationAttemptStatusDto.IN_PROGRESS]: 3,
    [RecommendationAttemptStatusDto.NOT_STARTED]: 4,
  };

  const sorted = orderBy(recommendationAttempts, [
    (attempt) => {
      return sortOrder[attempt.status];
    },
    (attempt) => {
      const submission = getAssessmentSubmissionByAttempt(
        attempt,
        attemptAssessmentSubmissions
      );
      if (!submission) {
        return null;
      }
      return new Date(submission.responseTime);
    },
  ], ['asc', 'asc']);

  return sorted[0];
};

export const getDueDate = (baseState: RemediationBaseState): string => {
  if (
    !baseState
    || !baseState.remediationAssignmentWithSources
    || !baseState.remediationAssignmentWithSources.eolsAssignment
  ) {
    return null;
  }
  return baseState.remediationAssignmentWithSources.eolsAssignment.dueDate;
};

export const getRecommendationFromAttempt = (recommendations: RecommendationDto[], attempt: RecommendationAttemptDto): RecommendationDto => {
  if (!recommendations || !attempt) {
    return null;
  }
  return recommendations.find((recommendation) => {
    return recommendation.id === attempt.recommendationId;
  });
};

const getAttemptCompletedDate = (
  attempt: RecommendationAttemptDto,
  baseState: RemediationBaseState
): string => {
  const recommendation = getRecommendationFromAttempt(baseState.recommendations, attempt);
  if (recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ) {
    if (attempt.dateCompleted) {
      return attempt.dateCompleted;
    }
    return null;
  }
  const submission = getAssessmentSubmissionByAttempt(
    attempt,
    baseState.attemptAssessmentSubmissions
  );
  if (!submission || !submission.responseTime) {
    return null;
  }
  return submission.responseTime;
};
export const getAttemptStatus = (
  attempt: RecommendationAttemptDto,
  baseState: RemediationBaseState
): StatusWithDueDate => {
  if (!attempt) {
    return StatusWithDueDate.NOT_STARTED;
  }

  if (attempt.status === RecommendationAttemptStatusDto.NOT_STARTED) {
    return StatusWithDueDate.NOT_STARTED;
  }

  if (attempt.status === RecommendationAttemptStatusDto.IN_PROGRESS) {
    return StatusWithDueDate.IN_PROGRESS;
  }

  const statusMap = {
    [RecommendationAttemptStatusDto.COMPLETED_FAILED]: StatusWithDueDate.COMPLETED_FAILED,
    [RecommendationAttemptStatusDto.COMPLETED_PASSED]: StatusWithDueDate.COMPLETED_PASSED,
    [StatusWithDueDate.COMPLETED_FAILED_PAST_DUE]: StatusWithDueDate.COMPLETED_FAILED_PAST_DUE,
    [StatusWithDueDate.COMPLETED_PASSED_PAST_DUE]: StatusWithDueDate.COMPLETED_PASSED_PAST_DUE,
  };

  const dueDate = getDueDate(baseState);
  const dateCompleted = getAttemptCompletedDate(attempt, baseState);

  if (
    !dueDate
    || new Date(dateCompleted) < new Date(dueDate)) {
    return statusMap[attempt.status];
  }
  return statusMap[`${attempt.status}_PAST_DUE`];
};

export const getRecommendationStatusWithDueDate = (props: {
  baseState: RemediationBaseState;
  recommendation: RecommendationDto;
}): StatusWithDueDate => {

  if (!props.baseState) {
    return null;
  }

  const {
    recommendationAttempts,
    attemptAssessmentSubmissions
  } = props.baseState;

  const bestAttempt = getBestStatusAttempt({
    recommendationAttempts: getAttemptsFromRecommendation(props.recommendation.id, recommendationAttempts),
    attemptAssessmentSubmissions
  });

  return getAttemptStatus(bestAttempt, props.baseState);
};

export const getAssessmentGoalStatusWithDueDate = (assessmentGoal: AssessmentGoalDto, baseState: RemediationBaseState): StatusWithDueDate => {
  const recommendations = getRecommendationsFromGoal(baseState.recommendations, assessmentGoal);
  const statuses = recommendations.map((recommendation) => {
    return getRecommendationStatusWithDueDate({
      baseState,
      recommendation
    });
  });

  const statusCountMap: Record<StatusWithDueDate, number> = statuses.reduce((acc, status) => {
    if (!status) {
      return acc;
    }
    return {
      ...acc,
      [status]: (acc[status] || 0) + 1
    };
  }, Object.values(StatusWithDueDate)
    .reduce((acc, status) => {
      return {
        ...acc,
        [status]: 0
      };
    }, {}) as Record<StatusWithDueDate, number>);

  if (statusCountMap[StatusWithDueDate.COMPLETED_PASSED] >= assessmentGoal.goal) {
    return StatusWithDueDate.COMPLETED_PASSED;
  }
  if (
    statusCountMap[StatusWithDueDate.COMPLETED_PASSED]
    + statusCountMap[StatusWithDueDate.COMPLETED_PASSED_PAST_DUE]
    >= assessmentGoal.goal
  ) {
    return StatusWithDueDate.COMPLETED_PASSED_PAST_DUE;
  }
  if (
    statusCountMap[StatusWithDueDate.IN_PROGRESS]
    + statusCountMap[StatusWithDueDate.COMPLETED_PASSED]
    + statusCountMap[StatusWithDueDate.COMPLETED_PASSED_PAST_DUE]
    + statusCountMap[StatusWithDueDate.COMPLETED_FAILED]
    + statusCountMap[StatusWithDueDate.COMPLETED_FAILED_PAST_DUE]
    >= 1
  ) {
    return StatusWithDueDate.IN_PROGRESS;
  }
  return StatusWithDueDate.NOT_STARTED;

};

export const getRecommendationTitle = (recommendation: RecommendationDto): string => {
  if (!recommendation) {
    return null;
  }
  if (!recommendation.title) {
    return `Missing Title: Recommendation ${recommendation.id}`;
  }
  return recommendation.title;
};

export const convertSecondsToMinutes = (seconds: number): number => {
  if (!seconds || seconds <= 0) {
    return 0;
  }
  const minutes = Math.floor(seconds / 60);
  return minutes > 0 ? minutes : 1;
};

export const getMinutesAndSecondsFromSeconds = (seconds: number): string => {
  if (seconds === 0) {
    return '0 sec';
  }
  const minute = Math.floor(seconds / 60);
  const second = seconds % 60;
  let result = '';
  if (minute > 0) {
    result += `${minute} min`;
  }
  if (second > 0) {
    if (result) {
      result += ' ';
    }
    result += `${second} sec`;
  }
  return result;
}

export const getHourAndMinuteFromSecond = (second: number): string => {

  if (second === 0) {
    return '0 min';
  }

  let minute = Math.ceil(second / 60);
  let hour = 0;

  if (minute === 0) {
    return '1 min';
  }

  if (minute >= 60) {
    hour = Math.floor(minute / 60);
    minute -= 60 * hour;

    if (minute === 0) {
      return `${hour} hr`;
    }

    return `${hour} hr ${minute} min`;
  }

  return `${minute} min`;
};

export const getHourAndMinuteFromMinute = (minute: number): string => {
  if (minute === 0) {
    return '0 min';
  }

  let hour = 0;

  if (minute >= 60) {
    hour = Math.floor(minute / 60);
    minute -= 60 * hour;

    if (minute === 0) {
      return `${hour} hr`;
    }

    return `${hour} hr ${minute} min`;
  }

  return `${minute} min`;
};

export const convertDurationToSeconds = (duration: string): number => {
  if (!duration) {
    return 0;
  }

  // Parse ISO 8601 duration format (e.g., "PT1H30M45S")
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);

  if (!match) {
    return 0;
  }

  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseFloat(match[3] || '0');

  return hours * 3600 + minutes * 60 + seconds;
};

export const getTotalTimeSpentFromAssessmentMetrics = (assessmentMetrics: RaasAssessmentMetricsDto): number => {
  if (!assessmentMetrics) {
    return 0;
  }

  const reviewSeconds = convertDurationToSeconds(assessmentMetrics.reviewContent?.timeSpent);
  const applySeconds = convertDurationToSeconds(assessmentMetrics.applyContent?.timeSpent);
  const assessSeconds = convertDurationToSeconds(assessmentMetrics.assessContent?.timeSpent);

  return reviewSeconds + applySeconds + assessSeconds;
};

export const getProgressDisplay = (metricCounts: ActivityTypeMetricsDto, activityType: RemediationActivityTypeDto): string => {

  if (!metricCounts) {
    return null;
  }

  if (activityType === RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE) {
    return `${metricCounts.activityTypeTotalGoalCompleted}/${metricCounts.activityTypeAttemptGoal} ${RemediationStatusCountLabel.CORRECT}`;
  }

  return `${metricCounts.activityTypeTotalGoalProgress}/${metricCounts.activityTypeTotalGoal} ${RemediationStatusCountLabel.COMPLETED}`;
};

const handleNavigateToCaseStudyApp = (props: NavigateToContentProps) => {

  const {
    appState,
    recommendation,
    recommendationAttempt,
    assessment,
    ref,
  } = props;

  const outPostBody: CaseStudyAppLinkOutBody = {
    assessmentVtwId: recommendation.contentId,
    instanceId: `RAAS_ATTEMPT_${recommendationAttempt.id}`,
    ref,
    raasAssessmentId: assessment.id,
    recommendationId: recommendation.id,
    recommendationAttemptId: recommendationAttempt.id,
    eolsUserId: recommendation.userId
  };

  const config = {
    action: AppAction.ASSESSMENT_START,
    app: Application.CASE_STUDY_APP,
    body: outPostBody,
    altSrcApp: Application.REMEDIATION,
    includeLinkHash: true,
    parentLinkId: getParentLinkIdForLinkOut(appState.appLinkData, appState.appLinkCookies),
  };

  appLinkOutAction({
    ...props,
    config,
  });
};

const handleNavigateToQuizApp = (props: NavigateToContentProps) => {
  const {
    quizSession,
    recommendation,
    recommendationAttempt,
    assessment,
    appState,
    ref
  } = props;

  const isInstructor = ref === RoutePath.REMEDIATION_STUDENT_DETAIL;
  const isSparkAssignment = getProduct(appState.remediationBaseState?.remediationAssignmentWithSources) === Product.SPARK;

  const outPostBody: AssessmentPlayerAppLinkOutBody = {
    ref,
    eolsUserId: recommendation.userId,
    quizSessionId: quizSession.id,
    assessmentId: quizSession.eolsAssessmentId,
    raasAssessmentId: assessment.id,
    recommendationId: recommendation.id,
    recommendationAttemptId: isInstructor ? null : recommendationAttempt.id,
    getNextQuestionAPI: `${ELSCommonConfig.buildUrl}/api/remediation/v1/quiz-session/next-question?assessmentId=${quizSession.eolsAssessmentId}`,
    submitQuestionAPI: `${ELSCommonConfig.buildUrl}/api/remediation/v1/quiz-session/submit-question`,
    assessmentPlayerConfig: {
      navigationType: 'Submit-Continue', // 'Next-Back',
      strictStudyMode: true,
      hideSubmissionConfirmationPage: true,
      hideMultipleChoicePrefixes: isSparkAssignment
    },
    integrationMetadata: {
      name: 'Personalized Learning Plan Quiz'
    }
  };

  appLinkOutAction({
    ...props,
    config: {
      action: AppAction.ASSESSMENT_START,
      app: Application.NHE_ASSESSMENT_PLAYER,
      body: outPostBody,
      includeLinkHash: true,
      altSrcApp: Application.REMEDIATION,
      parentLinkId: getParentLinkIdForLinkOut(appState.appLinkData, appState.appLinkCookies)
    }
  });
};
export const handleNavigateToContent = (props: NavigateToContentProps): void => {
  const {
    recommendation,
    recommendationAttempt,
    history,
    ref
  } = props;

  const isInstructor = ref === RoutePath.REMEDIATION_STUDENT_DETAIL;

  if (recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ) {
    handleNavigateToQuizApp(props);
    return;
  }

  if (recommendation.contentType === RecommendationContentTypeDto.HESI_CASE_STUDY) {
    handleNavigateToCaseStudyApp(props);
  }

  if (recommendation.contentType === RecommendationContentTypeDto.OSMOSIS_VIDEO) {
    history.push(addSearchParams(RoutePath.OSMOSIS_PLAYER, {
      recommendationAttemptId: isInstructor ? null : recommendationAttempt.id,
      recommendationId: recommendation.id,
      ref
    }));
  }

  if (recommendation.contentType === RecommendationContentTypeDto.EBOOK_READING) {
    history.push(addSearchParams(RoutePath.EBOOK_PLAYER, {
      recommendationAttemptId: isInstructor ? null : recommendationAttempt.id,
      recommendationId: recommendation.id,
      ref
    }));
  }

  if (recommendation.contentType === RecommendationContentTypeDto.TOPIC_OVERVIEW) {
    history.push(addSearchParams(RoutePath.TOPIC_OVERVIEW_PLAYER, {
      recommendationAttemptId: isInstructor ? null : recommendationAttempt.id,
      recommendationId: recommendation.id,
      ref
    }));
  }
};

export const handleNavigateToContentPerformance = (props: NavigateToContentProps): void => {

  if (props.recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ) {
    handleNavigateToContent(props);
    return;
  }

  const isInstructor = props.ref === RoutePath.REMEDIATION_STUDENT_DETAIL;
  const subTitle = isInstructor
    ? 'If the student attempted a quiz question multiple times, it will be listed multiple times in the report below.'
    : 'If you attempted a quiz question multiple times, it will be listed multiple times in the report below.';

  appLinkOutAction({
    appState: props.appState,
    appStateRef: props.appStateRef,
    setAppState: props.setAppState,
    config: {
      action: AppAction.ASSESSMENT_PERFORMANCE_VIEW,
      app: Application.NHE_ASSESSMENT_PLAYER,
      body: {
        targetPage: 'RAAS_QUIZ_RESULTS',
        resultsConfig: {
          getResultsAPI: `${ELSCommonConfig.buildUrl}/api/remediation/v1/attempt/${props.recommendationAttempt.id}/assess/results`,
          title: 'Personalized Learning Plan Quizzing',
          subTitle,
        },
        raasAssessmentId: props.assessment.id,
        eolsUserId: props.assessment.userId,
        recommendationAttemptId: props.recommendationAttempt.id,
        recommendationId: props.recommendationAttempt.recommendationId,
        ref: props.ref,
        ...(isInstructor && { eolsUserId: props.recommendation.userId })
      },
      altSrcApp: Application.REMEDIATION,
      parentLinkId: getParentLinkIdForLinkOut(props.appState.appLinkData, props.appState.appLinkCookies),
      includeLinkHash: true
    }
  });
};

export const getRecommendationContentId = (recommendation: RecommendationDto): string => {
  if (!recommendation) {
    return null;
  }
  return recommendation.contentId;
};

export const getNewAttemptAssessment = (recommendation: RecommendationDto): Partial<EolsAssessmentDto> => {
  const contentId = getRecommendationContentId(recommendation);
  if (!recommendation || !contentId) {
    return null;
  }
  return {
    assessmentGoals: [{
      goal: 1,
      text: recommendation.contentType,
      vtwId: contentId
    }],
    assessmentTopics: [],
    courseSectionId: recommendation.courseSectionId,
    type: RemediationContentTypeConfigMap[recommendation.contentType].assessmentType,
    contentId
  };
};

export const handleResumeOrStartNewAttempt = (props: AppContextState & {
  recommendation: RecommendationDto;
  recommendationAttempt: RecommendationAttemptDto;
  baseState: RemediationBaseState;
  history: History;
  ref: RoutePath;
}): Promise<void> => {

  const {
    recommendationAttempt,
    baseState,
    recommendation,
    ref
  } = props;

  if (ref === RoutePath.REMEDIATION_STUDENT_DETAIL) {
    handleNavigateToContent({
      ...props,
      recommendationAttempt: null,
      recommendation,
      assessment: baseState.eolsAssessment,
      quizSession: null,
      ref
    });
    return Promise.resolve();
  }

  if (recommendationAttempt && [
    RecommendationAttemptStatusDto.NOT_STARTED,
    RecommendationAttemptStatusDto.IN_PROGRESS
  ].includes(recommendationAttempt.status)) {
    trackNewRelicAction({
      appStateRef: props.appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.ATTEMPT_RESUMED,
        props: {
          recommendationId: recommendation.id,
          recommendationAttemptId: recommendationAttempt.id,
          contentType: recommendation.contentType,
          remediationAssignmentId: baseState.remediationAssignmentWithSources.remediationAssignment.id,
          assessmentId: baseState.eolsAssessment.id
        }
      }
    });
    handleNavigateToContent({
      ...props,
      assessment: baseState.eolsAssessment,
      quizSession: null,
      ref
    });
    return Promise.resolve();
  }

  const incompleteAttempts = getIncompleteAttempts(baseState.recommendationAttempts, recommendation);

  if (incompleteAttempts?.length) {
    trackNewRelicAction({
      appStateRef: props.appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.ATTEMPT_RESUMED,
        props: {
          recommendationId: recommendation.id,
          recommendationAttemptId: incompleteAttempts[0].id,
          contentType: recommendation.contentType,
          remediationAssignmentId: baseState.remediationAssignmentWithSources.remediationAssignment.id,
          assessmentId: baseState.eolsAssessment.id
        }
      }
    });
    handleNavigateToContent({
      ...props,
      recommendationAttempt: incompleteAttempts[0],
      recommendation,
      assessment: baseState.eolsAssessment,
      quizSession: null,
      ref
    });
    return Promise.resolve();
  }

  const newAssessment = getNewAttemptAssessment(recommendation);

  if (!newAssessment) {
    // eslint-disable-next-line no-alert
    alert('unable to construct new eolsAssessment');
    return Promise.resolve();
  }

  return postAssessmentAction({
    ...props,
    userId: props.appState.userId,
    assessment: newAssessment
  })
    .then((eolsAssessment) => {
      return postRecommendationAttemptAction({
        ...props,
        recommendationAttempt: {
          eolsAssessmentId: eolsAssessment.id,
          recommendationId: recommendation.id,
          status: RecommendationAttemptStatusDto.IN_PROGRESS
        }
      })
        .then((newAttempt) => {
          trackNewRelicAction({
            appStateRef: props.appStateRef,
            actionProps: {
              action: NewRelicAnalyticsAction.ATTEMPT_STARTED,
              props: {
                recommendationId: recommendation.id,
                recommendationAttemptId: newAttempt.id,
                contentType: recommendation.contentType,
                remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
                assessmentId: eolsAssessment.id
              }
            }
          });
          handleNavigateToContent({
            ...props,
            recommendationAttempt: newAttempt,
            recommendation,
            assessment: baseState.eolsAssessment,
            quizSession: null,
            ref
          });
        });
    });
};

export const filterGoalsByActivityType = (goals: AssessmentGoalDto[], activityType: RemediationActivityTypeDto): AssessmentGoalDto[] => {
  if (!goals || !activityType) {
    return [];
  }

  return goals.filter((goal) => {
    return goal.type === activityType;
  });
};

export const getActiveAssignmentActivityTypeSettings = (baseState: RemediationBaseState): RemediationActivityTypeSettings[] => {
  if (
    !baseState
    || !baseState.remediationAssignmentWithSources
    || !baseState.remediationAssignmentWithSources.remediationAssignment
    || !baseState.remediationAssignmentWithSources.remediationAssignment.settings
  ) {
    return [];
  }
  return baseState.remediationAssignmentWithSources.remediationAssignment.settings.activityTypes
    .filter((activityType: RemediationActivityTypeSettings) => {
      return activityType.isActive;
    })
    .sort((a: RemediationActivityTypeSettings, b: RemediationActivityTypeSettings) => {
      return ActivityTypeConfigMap[a.activityType].sortOrder - ActivityTypeConfigMap[b.activityType].sortOrder;
    });
};

export const getActiveContentTypes = (baseState: RemediationBaseState): RecommendationContentTypeDto[] => {
  if (
    !baseState
    || !baseState.remediationAssignmentWithSources
    || !baseState.remediationAssignmentWithSources.remediationAssignment
    || !baseState.remediationAssignmentWithSources.remediationAssignment.settings
  ) {
    return [];
  }
  return baseState.remediationAssignmentWithSources.remediationAssignment.settings.activityTypes
    .reduce((contentTypes, activityType: RemediationActivityTypeSettings): RecommendationContentTypeDto[] => {
      if (!activityType.isActive) {
        return contentTypes;
      }
      return uniq([
        ...contentTypes,
        ...activityType.contentSettings
          .filter((contentSetting) => {
            return contentSetting.isActive;
          })
          .map((contentSetting) => {
            return contentSetting.contentType;
          })
      ]);
    }, [])
    .sort((a: RecommendationContentTypeDto, b: RecommendationContentTypeDto) => {
      return RemediationContentTypeConfigMap[a].sortOrder - RemediationContentTypeConfigMap[b].sortOrder;
    });
};

export const isInstructorUser = (role: string): boolean => {
  return role === 'Ins';
};
export const tableDataRowHandler = (
  failSafe: [boolean, React.ReactElement] | [],
  objToReturn: JSX.Element
): React.ReactElement => {
  if (failSafe.length !== 0 && !!failSafe[0]) return failSafe[1];
  return objToReturn;
};

export const getGoalBreadcrumbs = (goal: AssessmentGoalDto, categoryGroupName?: string | null): GoalBreadcrumb => {
  if (!goal || !goal.text) {
    return {
      text: null,
      path: [],
      pathDisplay: null,
      pathFull: [],
      pathFullDisplay: null,
    }
  }
  const pathFull = goal.text.split('__').map((text) => text.trim());
  const path = [...pathFull];
  path.pop();
  if (path.length === 1 && categoryGroupName && path[0].toLowerCase() === categoryGroupName.toLowerCase()) {
    return {
      text: pathFull[pathFull.length - 1],
      path: [],
      pathDisplay: '',
      pathFull,
      pathFullDisplay: pathFull.join(' • ')
    };
  }
  return {
    text: pathFull[pathFull.length - 1],
    path,
    pathDisplay: path.join(' • '),
    pathFull,
    pathFullDisplay: pathFull.join(' • ')
  };
}

export const getAssessmentIdForInit = (appState: AppState): number => {
  if (appState.selectedRaasAssessmentDto) {
    return appState.selectedRaasAssessmentDto.studentStudyEolsAssessmentId;
  }

  return appState.appLinkData.targetApp === Application.REMEDIATION
    ? appState.appLinkData.outPostBody?.assessmentId
    : appState.appLinkData.outPostBody?.raasAssessmentId;
};

export const getUserIdForInit = (appState: AppState): string => {
  if (appState.selectedRaasAssessmentDto) {
    return appState.selectedRaasAssessmentDto.userId.toString();
  }

  const userId = appState.appLinkData.outPostBody?.eolsUserId || appState?.userId;
  return userId.toString();
};

export const getRecommendationById = (appStateRef: React.MutableRefObject<AppState>, id: number | string): RecommendationDto => {
  const recommendations = appStateRef?.current?.remediationBaseState?.recommendations;
  if (!recommendations || id == null) {
    return null;
  }
  return recommendations.find((recommendation) => recommendation.id === Number(id));
};

export const getRecommendationAttemptById = (appStateRef: React.MutableRefObject<AppState>, id: number | string): RecommendationAttemptDto => {
  const recommendationAttempts = appStateRef?.current?.remediationBaseState?.recommendationAttempts;
  if (!recommendationAttempts || id == null) {
    return null;
  }
  return recommendationAttempts.find((attempt) => attempt.id === Number(id));
};

export const getContentTypePassNumber = (recommendation: RecommendationDto, baseState: RemediationBaseState): number => {
  const {
    remediationAssignmentWithSources,
  } = baseState;

  if (!recommendation) {
    return null;
  }
  if (
    !remediationAssignmentWithSources
    || !remediationAssignmentWithSources.remediationAssignment
    || !remediationAssignmentWithSources.remediationAssignment.settings
    || !remediationAssignmentWithSources.remediationAssignment.settings.activityTypes
  ) {
    return null;
  }

  const { remediationAssignment } = remediationAssignmentWithSources;

  if (recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ) {
    return baseState.quizAttemptDetails?.correctQuestionThreshold;
  }

  const activityTypeConfig = remediationAssignment.settings.activityTypes.find((_activityTypeConfig: HesiExamRemediationSettingsDto['activityTypes'][0]) => {
    // TODO: fix this after https://elsevier.atlassian.net/browse/FLOR-1615
    return _activityTypeConfig.activityType.includes(RemediationContentTypeConfigMap[recommendation.contentType].activityType);
  });

  if (!activityTypeConfig) {
    return null;
  }

  const contentSettingConfig = activityTypeConfig.contentSettings.find((contentSetting) => {
    return contentSetting.contentType === recommendation.contentType;
  });

  if (!contentSettingConfig) {
    return null;
  }

  return contentSettingConfig.minScoreToPass;

};

export const getUniqueCorrectQuestionCount = (questionSubmissionDetails: QuestionSubmissionDetailsDto[]): number => {
  if (!questionSubmissionDetails || !questionSubmissionDetails.length) {
    return 0;
  }

  const uniqueQuestions = questionSubmissionDetails
    .flatMap(detail =>
      detail.questions
        .filter(question => question.correct)
        .map(question => question.questionVtwId))
    .reduce((acc, questionVtwId) => {
      acc.add(questionVtwId);
      return acc;
    }, new Set<string>());

  return uniqueQuestions.size;
};

export const getUniqueAttemptedQuestionCount = (questionSubmissionDetails: QuestionSubmissionDetailsDto[]): number => {
  if (!questionSubmissionDetails || !questionSubmissionDetails.length) {
    return 0;
  }

  const uniqueQuestions = questionSubmissionDetails
    .flatMap(detail => detail.questions.map(question => question.questionVtwId))
    .reduce((acc, questionVtwId) => {
      acc.add(questionVtwId);
      return acc;
    }, new Set<string>());

  return uniqueQuestions.size;
};

export const getAssessmentScoreDetails = (props: {
  recommendation: RecommendationDto;
  submission: AssessmentSubmissionDto | null;
  scoreToPass: number;
  baseState: RemediationBaseState;
}): {
  score: number;
  passed: boolean;
  assignmentScore: string;
  assignmentNumericGrade: string;
} | null => {
  const {
    recommendation,
    submission,
    scoreToPass,
    baseState
  } = props;

  if (recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ) {
    if (!submission) {
      return null;
    }
    return {
      score: submission.score,
      passed: submission.score >= scoreToPass,
      assignmentScore: submission.score.toString(),
      assignmentNumericGrade: submission.score >= scoreToPass ? '1' : '0'
    };
  }

  if (!baseState?.quizAttemptDetails) {
    return null;
  }
  const { questionSubmissionDetails } = baseState.quizAttemptDetails;
  if (!questionSubmissionDetails || !questionSubmissionDetails.length) {
    return null;
  }
  const totalQuestions = baseState.quizAttemptDetails.totalItems;
  const totalQuestionsAttempted = getUniqueAttemptedQuestionCount(questionSubmissionDetails);
  const totalQuestionsCorrect = getUniqueCorrectQuestionCount(questionSubmissionDetails);
  const score = totalQuestionsCorrect / totalQuestions;
  return {
    score,
    passed: score >= scoreToPass && totalQuestionsAttempted == totalQuestions,
    assignmentScore: score.toString(),
    assignmentNumericGrade: (totalQuestionsCorrect >= baseState.quizAttemptDetails.minScoreToPass && totalQuestionsAttempted == totalQuestions) ? '1' : '0'
  };
};

export const getQuizSessionResults = (
  baseState: RemediationBaseState,
  outPostBody: RecordObject
): { totalQuestions: number; totalCorrect: number } => {

  if (
    !baseState
    || !baseState.quizAttemptDetails
    || !baseState.quizAttemptDetails.questionSubmissionDetails
  ) {
    return null;
  }

  if (!outPostBody.quizSessionId) {
    return null;
  }

  const {
    quizSessionId
  } = outPostBody;

  const { questionSubmissionDetails } = baseState.quizAttemptDetails;

  if (!questionSubmissionDetails || !questionSubmissionDetails.length) {
    return null;
  }

  const quizSessionResult = questionSubmissionDetails.find((result) => {
    return result.quizSessionId === quizSessionId;
  });

  if (!quizSessionResult) {
    return null;
  }
  const totalQuestions = quizSessionResult.questions.length;
  const totalCorrect = quizSessionResult.questions.filter((question) => question.correct).length;
  return {
    totalQuestions,
    totalCorrect
  };
};

export const isGoalComplete = (metricCounts: ActivityTypeMetricsDto): boolean => {
  if (!metricCounts) {
    return false;
  }

  if (metricCounts.activityTypeTotalGoal === 0) {
    return true;
  }

  if (!metricCounts.activityTypeTotalGoal || !metricCounts.activityTypeAttemptGoal) {
    return false;
  }

  return metricCounts.activityTypeTotalGoalCompleted >= metricCounts.activityTypeTotalGoal
    && metricCounts.activityTypeAttemptGoalCompleted >= metricCounts.activityTypeAttemptGoal;
};

/**
 * Whether EVERY Review (REVIEW_BY_TAXON) assessment goal is fully complete.
 * Returns false when there are no Review goals (nothing to gate on). Used by
 * getNextRecommendation to stop surfacing Review recs once the student has
 * finished all Review requirements, moving them on to Apply / Assess. FLOR-1632.
 */
export const areAllReviewGoalsComplete = (baseState: RemediationBaseState): boolean => {
  const assessmentGoals = baseState?.eolsAssessment?.assessmentGoals;
  if (!assessmentGoals || !assessmentGoals.length) {
    return false;
  }
  const reviewGoals = assessmentGoals.filter((goal) => goal.type === RemediationActivityTypeDto.REVIEW_BY_TAXON);
  if (!reviewGoals.length) {
    return false;
  }
  return reviewGoals.every((goal) => {
    const metrics = getAssessmentGoalTotalsCombined({ assessmentGoal: goal, baseState });
    return isGoalComplete(metrics);
  });
};

export const getGoalByVtwId = (assessmentGoals: AssessmentGoalDto[], vtwId: string): AssessmentGoalDto => {
  if (!assessmentGoals || !vtwId) {
    return null;
  }
  return assessmentGoals.find((goal) => {
    return goal.vtwId === vtwId;
  });
};

export const getGoalFromRecommendation = (assessmentGoals: AssessmentGoalDto[], recommendation: RecommendationDto): AssessmentGoalDto => {
  if (!recommendation || !assessmentGoals) {
    return null;
  }
  return assessmentGoals.find((goal) => {
    return goal.vtwId === recommendation.assessmentGoalVtwId && goal.type === recommendation.assessmentGoalType;
  });
};

export const getAssessmentGoalFromRecommendation = (assessment: EolsAssessmentDto, recommendation: RecommendationDto): AssessmentGoalDto => {
  if (!assessment) {
    return null;
  }
  return getGoalFromRecommendation(assessment.assessmentGoals, recommendation);
};

export const sortAssessmentGoals = (assessmentGoals: AssessmentGoalDto[]): AssessmentGoalDto[] => {
  return orderBy(assessmentGoals, [(goal) => {
    if (!ActivityTypeConfigMap[goal.type]) {
      // eslint-disable-next-line no-console
      console.warn(`ActivityTypeConfigMap[${goal.type}] is not defined`);
      return 999;
    }
    return ActivityTypeConfigMap[goal.type as RemediationActivityTypeDto].sortOrder;
  }, 'goal', 'text'], ['asc', 'desc', 'asc']);
};

export const sortRecommendations = (recommendations: RecommendationDto[], assessmentGoals: AssessmentGoalDto[]): RecommendationDto[] => {
  const sortedGoals = sortAssessmentGoals(assessmentGoals);
  return orderBy(recommendations, [(recommendation) => {
    return sortedGoals.findIndex((goal) => {
      return isRecommendationPartOfGoal(recommendation, goal);
    });
  }, 'displayOrder', 'id'], ['asc', 'asc', 'asc']);
};

export const getIncompleteRecommendations = (
  recommendations: RecommendationDto[],
  recommendationAttempts: RecommendationAttemptDto[],
  skipRecommendationIds: number[] = []
): RecommendationDto[] => {

  if (!recommendations) {
    return []
  }

  return recommendations.filter((recommendation) => {
    if (skipRecommendationIds?.includes(recommendation.id)) {
      return false;
    }
    const attempts = getAttemptsFromRecommendation(recommendation.id, recommendationAttempts);
    if (!attempts) {
      return true
    }
    return attempts.every((attempt) => {
      return attempt.status !== RecommendationAttemptStatusDto.COMPLETED_PASSED
    })
  });
};

/**
 * The assessment goals that are not yet complete by their metrics (isGoalComplete).
 */
export const getIncompleteGoals = (baseState: RemediationBaseState): AssessmentGoalDto[] => {
  const assessmentGoals = baseState?.eolsAssessment?.assessmentGoals;
  if (!assessmentGoals) {
    return [];
  }
  return assessmentGoals.filter((goal) => {
    const statusCounts = getAssessmentGoalTotalsCombined({ assessmentGoal: goal, baseState });
    return !isGoalComplete(statusCounts);
  });
};

/**
 * Incomplete recommendations that belong to a goal that is itself still incomplete.
 *
 * A goal can be complete by its metrics while individual recommendations under it
 * remain incomplete; those recs must NOT be offered as the next/second rec. This is
 * the shared candidate pool for both getNextRecommendation and getSecondRecommendation.
 */
export const getIncompleteRecommendationsForIncompleteGoals = (
  baseState: RemediationBaseState,
  skipRecommendationIds: number[] = []
): RecommendationDto[] => {
  const incompleteGoals = getIncompleteGoals(baseState);
  if (!incompleteGoals.length) {
    return [];
  }
  const incompleteRecommendations = getIncompleteRecommendations(
    baseState?.recommendations,
    baseState?.recommendationAttempts,
    skipRecommendationIds
  );
  return incompleteRecommendations.filter((recommendation) => {
    return getGoalFromRecommendation(incompleteGoals, recommendation);
  });
};

export const getNextRecommendation = (
  baseState: RemediationBaseState,
  assessmentGoal: AssessmentGoalDto,
  skipRecommendationIds: number[] = []
): RecommendationDto => {
  if (!baseState || !baseState.recommendations || !baseState.eolsAssessment) {
    return null;
  }

  const incompleteRecommendationsByGoal = getIncompleteRecommendationsForIncompleteGoals(baseState, skipRecommendationIds);

  if (!incompleteRecommendationsByGoal.length) {
    return null;
  }

  // FLOR-1632: once every Review goal is complete, never surface another Review
  // recommendation — move the student on to Apply (Case Study) / Assess (Quizzing).
  const eligibleRecommendations = areAllReviewGoalsComplete(baseState)
    ? incompleteRecommendationsByGoal.filter((recommendation) => recommendation.assessmentGoalType !== RemediationActivityTypeDto.REVIEW_BY_TAXON)
    : incompleteRecommendationsByGoal;

  if (!eligibleRecommendations.length) {
    return null;
  }

  // Only prioritize current goal recs for review type
  if (assessmentGoal && assessmentGoal.type === RemediationActivityTypeDto.REVIEW_BY_TAXON) {
    const currentGoalRecommendations = eligibleRecommendations.filter((recommendation) => {
      return isRecommendationPartOfGoal(recommendation, assessmentGoal);
    });
    if (currentGoalRecommendations?.length) {
      return sortRecommendations(currentGoalRecommendations, baseState.eolsAssessment.assessmentGoals)[0];
    }
  }

  return sortRecommendations(eligibleRecommendations, baseState.eolsAssessment.assessmentGoals)[0];
};

export const getCompletedDate = (baseState: RemediationBaseState): string => {
  if (
    !baseState
    || !baseState.raasAssessmentSubmissions
    || !baseState.raasAssessmentSubmissions.length
    || !baseState.raasAssessmentSubmissions[0].responseTime
  ) {
    return null;
  }

  return baseState.raasAssessmentSubmissions[0].responseTime;
};
